import React, { useState, useMemo } from "react";
import { SegmentedControl } from "@deere/isg.component-library";
import "./AutonomyTimeline.css";

interface AutonomyLogEntry {
  _id: string;
  metadata: {
    machineIdentifier: string;
    source: string;
  };
  timestamp: string;
  state: number;
}

// VADC state constants (from machine.proto)
const VADC_STATE = {
  OFF: 0,
  STANDBY: 1,
  ACTIVE: 2,
  LOW_ENERGY: 3,
  INVALID: 255,
};

// Navigator state constants (from machine.proto NavigatorStateEnum)
const NAV_STATE = {
  OFF: 0,
  GENERATING_PATH: 1,
  READY: 2,
  ENGAGED: 3,
  INTERRUPTED: 4,
};

type DisplayState = "off" | "ready" | "paused" | "working";

interface TimelineSegment {
  startTime: number;
  endTime: number;
  displayState: DisplayState;
}

const STATE_COLORS: Record<DisplayState, string> = {
  working: "#2196f3",
  ready: "#4caf50",
  paused: "#ff9800",
  off: "#9e9e9e",
};

const STATE_LABELS: Record<DisplayState, string> = {
  working: "Working",
  ready: "Ready",
  paused: "Paused",
  off: "Off",
};

const TIME_RANGE_OPTIONS = [
  { label: "1h", ms: 60 * 60 * 1000 },
  { label: "4h", ms: 4 * 60 * 60 * 1000 },
  { label: "12h", ms: 12 * 60 * 60 * 1000 },
  { label: "24h", ms: 24 * 60 * 60 * 1000 },
  { label: "All", ms: 0 },
];

function getDisplayState(source: string, state: number): DisplayState {
  if (source === "navigator") {
    if (state === NAV_STATE.READY) return "ready";
    if (state === NAV_STATE.INTERRUPTED) return "paused";
    return "working";
  }
  // VADC
  if (state === VADC_STATE.STANDBY) return "ready";
  if (state === VADC_STATE.ACTIVE) return "working";
  return "off";
}

function formatTimeLabel(ms: number, showDate: boolean): string {
  const date = new Date(ms);
  const time = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (showDate) {
    const dateStr = date.toLocaleDateString([], { month: "short", day: "numeric" });
    return `${dateStr} ${time}`;
  }
  return time;
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / (3600 * 24));
  const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function AutonomyTimeline({ autonomyLogs }: { autonomyLogs: AutonomyLogEntry[] }) {
  const [selectedRangeIndex, setSelectedRangeIndex] = useState(0);

  // Parse and sort logs by timestamp ascending
  const processedLogs = useMemo(() => {
    return autonomyLogs
      .map((log) => ({
        timestamp: new Date(log.timestamp).getTime(),
        source: log.metadata?.source?.toLowerCase() || "vadc",
        state: typeof log.state === "number" ? log.state : 0,
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [autonomyLogs]);

  // Compute the visible time window based on selected range
  const { rangeStart, rangeEnd } = useMemo(() => {
    if (processedLogs.length === 0) return { rangeStart: 0, rangeEnd: 0 };

    const now = Date.now();
    const rangeDef = TIME_RANGE_OPTIONS[selectedRangeIndex];

    if (rangeDef.ms === 0) {
      // "All" — show from earliest log to now
      return { rangeStart: processedLogs[0].timestamp, rangeEnd: now };
    }

    return { rangeStart: now - rangeDef.ms, rangeEnd: now };
  }, [processedLogs, selectedRangeIndex]);

  // Build timeline segments within the visible window
  const segments = useMemo(() => {
    if (processedLogs.length === 0 || rangeStart === 0) return [];

    const result: TimelineSegment[] = [];

    // Find the last log at or before rangeStart — this is the state that
    // was "active" when the visible window begins.
    let activeLogIndex = -1;
    for (let i = 0; i < processedLogs.length; i++) {
      if (processedLogs[i].timestamp <= rangeStart) {
        activeLogIndex = i;
      } else {
        break;
      }
    }

    // Gather the log active at rangeStart plus every log inside the window
    const relevantLogs: typeof processedLogs = [];

    if (activeLogIndex >= 0) {
      relevantLogs.push(processedLogs[activeLogIndex]);
    }

    for (
      let i = Math.max(0, activeLogIndex + 1);
      i < processedLogs.length;
      i++
    ) {
      if (processedLogs[i].timestamp >= rangeEnd) break;
      relevantLogs.push(processedLogs[i]);
    }

    if (relevantLogs.length === 0) return [];

    // Convert each log into a segment that spans until the next log (or rangeEnd)
    for (let i = 0; i < relevantLogs.length; i++) {
      const log = relevantLogs[i];
      const displayState = getDisplayState(log.source, log.state);

      const segStart = Math.max(log.timestamp, rangeStart);
      const segEnd =
        i < relevantLogs.length - 1
          ? Math.min(relevantLogs[i + 1].timestamp, rangeEnd)
          : rangeEnd;

      if (segEnd > segStart) {
        result.push({ startTime: segStart, endTime: segEnd, displayState });
      }
    }

    return result;
  }, [processedLogs, rangeStart, rangeEnd]);

  // Axis labels along the bottom of the timeline bar
  const totalDuration = rangeEnd - rangeStart;
  const showDate = totalDuration > 12 * 60 * 60 * 1000;

  const timeLabels = useMemo(() => {
    if (rangeStart === 0 || rangeEnd === 0) return [];

    const labelCount = 4;
    const labels: { position: string; text: string }[] = [];

    for (let i = 0; i <= labelCount; i++) {
      const time = rangeStart + (totalDuration * i) / labelCount;
      labels.push({
        position: (i / labelCount) * 100 + "%",
        text: formatTimeLabel(time, showDate),
      });
    }

    return labels;
  }, [rangeStart, rangeEnd, totalDuration, showDate]);

  // Per-state duration totals within the visible window
  const rangeSummary = useMemo(() => {
    const summary: Record<DisplayState, number> = {
      off: 0,
      ready: 0,
      paused: 0,
      working: 0,
    };
    let total = 0;

    segments.forEach((seg) => {
      const duration = seg.endTime - seg.startTime;
      summary[seg.displayState] += duration;
      total += duration;
    });

    return { ...summary, total };
  }, [segments]);

  if (autonomyLogs.length === 0) return null;

  const segmentedControlButtons = TIME_RANGE_OPTIONS.map((opt, idx) => ({
    name: opt.label,
    onClickHandler: () => setSelectedRangeIndex(idx),
  }));

  return (
    <div className="autonomy-timeline">
      <h3>Autonomy Timeline</h3>

      {/* Time range selector — ISG SegmentedControl */}
      <div className="timeline-range-selector">
        <SegmentedControl
          buttons={segmentedControlButtons}
          selectedIndex={selectedRangeIndex}
        />
      </div>

      {segments.length > 0 ? (
        <>
          {/* Segmented timeline bar */}
          <div className="timeline-bar">
            {segments.map((seg, i) => {
              const widthPercent =
                ((seg.endTime - seg.startTime) / totalDuration) * 100;
              const leftPercent =
                ((seg.startTime - rangeStart) / totalDuration) * 100;

              return (
                <div
                  key={i}
                  className="timeline-bar-segment"
                  style={{
                    left: leftPercent + "%",
                    width: widthPercent + "%",
                    backgroundColor: STATE_COLORS[seg.displayState],
                  }}
                  title={`${STATE_LABELS[seg.displayState]}: ${formatTimeLabel(seg.startTime, showDate)} – ${formatTimeLabel(seg.endTime, showDate)} (${formatDuration(seg.endTime - seg.startTime)})`}
                />
              );
            })}
          </div>

          {/* Time axis */}
          <div className="timeline-axis">
            {timeLabels.map((label, i) => (
              <span
                key={i}
                className="timeline-axis-label"
                style={{ left: label.position }}
              >
                {label.text}
              </span>
            ))}
          </div>
        </>
      ) : (
        <p className="timeline-no-data">No data in selected range.</p>
      )}

      {/* Legend with per-state percentages */}
      <div className="timeline-legend">
        {(["working", "ready", "paused", "off"] as DisplayState[]).map(
          (state) => (
            <div key={state} className="timeline-legend-item">
              <span
                className="timeline-legend-color"
                style={{ backgroundColor: STATE_COLORS[state] }}
              />
              <span className="timeline-legend-label">
                {STATE_LABELS[state]}
              </span>
              {rangeSummary.total > 0 && (
                <span className="timeline-legend-percent">
                  {(
                    (rangeSummary[state] / rangeSummary.total) *
                    100
                  ).toFixed(0)}
                  %
                </span>
              )}
            </div>
          ),
        )}
      </div>
    </div>
  );
}

export default AutonomyTimeline;
